import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Clock, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useCurrentMemberId } from "@/hooks/useEventRsvp";

interface RsvpRow {
  id: string;
  event_id: string;
  occurrence_date: string;
  events: {
    title: string;
    location: string;
    start_time: string;
    end_time: string;
    is_special: boolean;
  } | null;
}

const formatTime12h = (time: string): string => {
  const date = new Date(`2000-01-01T${time}`);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

const parseLocalDate = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const MyEventsList = () => {
  const { memberId, loading: memberLoading } = useCurrentMemberId();
  const [rsvps, setRsvps] = useState<RsvpRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRsvps = async () => {
    if (!memberId) {
      setRsvps([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("event_rsvps")
      .select("id, event_id, occurrence_date, events(title, location, start_time, end_time, is_special)")
      .eq("member_id", memberId)
      .order("occurrence_date", { ascending: false });
    if (error) {
      toast.error("Failed to load your events");
    } else {
      setRsvps((data as any) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!memberLoading) fetchRsvps();
  }, [memberId, memberLoading]);

  const handleCancel = async (rsvpId: string) => {
    if (!confirm("Cancel this RSVP?")) return;
    const { error } = await supabase.from("event_rsvps").delete().eq("id", rsvpId);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("RSVP canceled");
    fetchRsvps();
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcoming = rsvps.filter((r) => parseLocalDate(r.occurrence_date) >= today);
  const past = rsvps.filter((r) => parseLocalDate(r.occurrence_date) < today);

  if (memberLoading || loading) {
    return <p className="text-center text-muted-foreground py-8">Loading...</p>;
  }

  if (!memberId) {
    return (
      <Card>
        <CardContent className="pt-6 text-center text-muted-foreground">
          Member profile not found. Contact an admin if this seems wrong.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Upcoming Events
          </CardTitle>
          <CardDescription>
            Events you're going to. Cancel any time if your plans change.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">
              No upcoming RSVPs. Head over to the events page to mark yourself as going.
            </p>
          ) : (
            <ul className="divide-y">
              {upcoming.map((r) => (
                <RsvpItem key={r.id} rsvp={r} onCancel={handleCancel} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {past.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Past Attendance</CardTitle>
            <CardDescription>{past.length} previous {past.length === 1 ? "meeting" : "meetings"} attended</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {past.slice(0, 10).map((r) => (
                <RsvpItem key={r.id} rsvp={r} past />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const RsvpItem = ({
  rsvp, onCancel, past,
}: {
  rsvp: RsvpRow;
  onCancel?: (id: string) => void;
  past?: boolean;
}) => {
  const date = parseLocalDate(rsvp.occurrence_date);
  return (
    <li className="py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{rsvp.events?.title || "Event"}</p>
        <div className="text-sm text-muted-foreground space-y-1 mt-1">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {format(date, "EEEE, MMMM d, yyyy")}
          </div>
          {rsvp.events && (
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {formatTime12h(rsvp.events.start_time)} – {formatTime12h(rsvp.events.end_time)}
            </div>
          )}
          {rsvp.events?.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {rsvp.events.location}
            </div>
          )}
        </div>
      </div>
      {!past && onCancel && (
        <Button variant="ghost" size="sm" onClick={() => onCancel(rsvp.id)}>
          <X className="h-4 w-4" />
        </Button>
      )}
    </li>
  );
};

export default MyEventsList;
